/**
 * Волна C (docs/MIGRATION_ROUTER.md §5). Было: <PrivateRoute path='/supplier/:id'>.
 */
import { createFileRoute, notFound } from '@tanstack/react-router';
import CacheDataRoute from '../../CacheDataRoute';
import SupplierPage from '@routes/SupplierPage';
import { getQueryClient } from '../../providers/query';
import { productsBySupplierInfiniteQueryOptions } from '@queries/index';
import { CATALOG_EPP_DEFAULT } from '../../routes/Catalog/components/CatalogCategory/catalogInitialParams';
import { isContentNotFoundError } from '../../api/contentNotFound';

export const Route = createFileRoute('/supplier/$id')({
  /**
   * ЭТАП 2 — честный 404 страницы поставщика.
   *
   * 1. Нечисловой id (/supplier/abc) отсекаем без запроса: компонент и раньше
   *    считал такой адрес невалидным (checkIfUrlHasNonNumerics) и рисовал
   *    баннер «Не найдено», но со статусом 200.
   * 2. Несуществующий поставщик — теперь тоже 404. Бэк
   *    (catalogResolver.getProductsBySupplierId) получил честную реализацию:
   *    `if (!count) throw new ContentNotFoundError(...)` плюс
   *    PaginationHelper.checkPageExists на странице за последней. Раньше он
   *    отдавал пустой список, отличить «нет поставщика» от «нет товаров
   *    в наличии» было нельзя, и страница оставалась 200 с баннером
   *    «Поиск не дал результатов» — этот компромисс снят.
   *
   * Ключ обязан совпадать с root-префетчем (pristine: setLimit
   * CATALOG_EPP_DEFAULT, xStoreId null) — тогда ensureInfiniteQueryData
   * дедуплицируется и лишнего запроса нет. Loader'ы идут ПАРАЛЛЕЛЬНО
   * префетчу, поэтому читать состояние кэша недостаточно: сначала короткое
   * замыкание на уже осевшей ошибке, иначе ждём тот же промис.
   */
  loader: async ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw notFound();
    }
    if (typeof window !== 'undefined') {
      return;
    }
    const queryClient = getQueryClient();
    const options = productsBySupplierInfiniteQueryOptions({
      supplierId: Number(params.id),
      setLimit: CATALOG_EPP_DEFAULT,
      xStoreId: null
    });
    // Префетч успел упасть раньше — ошибка уже в кэше, без второго запроса
    const settled = queryClient.getQueryState(options.queryKey);
    if (settled?.error) {
      if (isContentNotFoundError(settled.error)) {
        throw notFound();
      }
      return;
    }
    try {
      await queryClient.ensureInfiniteQueryData(options);
    } catch (error) {
      if (isContentNotFoundError(error)) {
        throw notFound();
      }
    }
  },
  component: () => <CacheDataRoute component={SupplierPage} path='/supplier/:id' />
});
